import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, NavLink, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  LayoutDashboard, Users, CreditCard, MessageSquare, Settings, 
  ChevronRight, Plus, Edit2, Trash2, Download, Upload, Send,
  DollarSign, TrendingUp, Cpu, AlertCircle, Check, X, Menu,
  ExternalLink, RefreshCw, FileSpreadsheet, Shield, LogOut, Bot, Receipt as ReceiptIcon,
  Wrench, History, Package, FileText
} from "lucide-react";

// Import auth
import { useAuth, LoginScreen } from "./components/Auth";

// Import pages
import Dashboard from "./pages/Dashboard";
import Customers from "./pages/Customers";
import Payments from "./pages/Payments";
import WhatsAppBot from "./pages/WhatsAppBot";
import Receipts from "./pages/Receipts";
import SettingsPage from "./pages/Settings";
import AdminPanel from "./pages/AdminPanel";
import PendingMachines from "./pages/PendingMachines";
import MachineStatements from "./pages/MachineStatements";
import WorkerHistory from "./pages/WorkerHistory";
import CustomerLogin from "./pages/CustomerLogin";
import Catalog from "./pages/Catalog";
import CustomerDashboard from "./pages/CustomerDashboard";
import CustomerPortal from "./pages/CustomerPortal";
import HelperPortal from "./pages/HelperPortal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// ---- Admin session wiring ----
// Every axios call to our backend automatically carries the admin session token.
// Public endpoints (customer portal, helper portal) simply ignore it.
const AUTH_STORAGE_KEY = "wkbeast_auth_token";
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem(AUTH_STORAGE_KEY);
  if (token && config.url && config.url.includes("/api")) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});
// If the server says the session is no longer valid (expired / revoked), clear it
// and show the login screen — but never for portal/helper/login calls, which have
// their own 401 meanings (e.g. a customer typing a wrong password).
axios.interceptors.response.use(
  (res) => res,
  (err) => {
    const url = err?.config?.url || "";
    const ownAuthArea =
      url.includes("/portal/") || url.includes("/helper/") || url.includes("/admin/login");
    if (
      err?.response?.status === 401 &&
      !ownAuthArea &&
      localStorage.getItem(AUTH_STORAGE_KEY)
    ) {
      localStorage.removeItem(AUTH_STORAGE_KEY);
      window.location.href = "/";
    }
    return Promise.reject(err);
  }
);

const _fmtDeploy = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? "—" : d.toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
};

// When the website and the server were last updated, to confirm a deploy
// landed without opening Netlify or asking.
const DeployInfo = () => {
  const [server, setServer] = useState(null);
  useEffect(() => {
    axios.get(`${API}/version`).then((r) => setServer(r.data)).catch(() => setServer({}));
  }, []);
  const webCommit = process.env.REACT_APP_COMMIT;
  return (
    <div className="text-[10px] leading-relaxed text-gray-500" data-testid="deploy-info">
      <div title="When this website build was made (Netlify)">
        Website: {_fmtDeploy(process.env.REACT_APP_BUILD_TIME)}{webCommit ? ` · ${webCommit}` : ""}
      </div>
      <div title="When the server was last restarted with new code">
        Server: {server ? _fmtDeploy(server.started_at) : "…"}{server?.commit ? ` · ${server.commit}` : ""}
      </div>
    </div>
  );
};

// Sidebar Component
const Sidebar = ({ isOpen, setIsOpen, onLogout }) => {
  const location = useLocation();
  
  // Sidebar grouped by purpose. Each link keeps its existing route — only label & order changed.
  const sections = [
    {
      heading: "Overview",
      links: [
        { path: "/", icon: LayoutDashboard, label: "Dashboard" },
      ],
    },
    {
      heading: "Customers & Machines",
      links: [
        { path: "/customers", icon: Users, label: "Customers" },
        { path: "/admin", icon: Wrench, label: "Machines & Repairs" },
        { path: "/pending-machines", icon: Package, label: "Pending Machines" },
        { path: "/machine-statements", icon: FileText, label: "Machine Statements" },
        { path: "/worker-history", icon: History, label: "Worker/IP History" },
      ],
    },
    {
      heading: "Money",
      links: [
        { path: "/payments", icon: CreditCard, label: "Payments" },
        { path: "/receipts", icon: ReceiptIcon, label: "Receipts" },
      ],
    },
    {
      heading: "Automation",
      links: [
        { path: "/whatsapp-bot", icon: Bot, label: "Bot Console" },
      ],
    },
    {
      heading: "System",
      links: [
        { path: "/settings", icon: Settings, label: "Settings" },
      ],
    },
  ];

  const handleLogout = () => {
    if (window.confirm("Are you sure you want to logout?")) {
      onLogout();
    }
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}
      
      <aside className={`sidebar ${isOpen ? 'open' : ''}`}>
        <div className="p-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#00E054] flex items-center justify-center">
              <span className="text-black font-bold text-lg">W</span>
            </div>
            <div>
              <h1 className="font-bold text-lg text-white">WKBeast</h1>
              <p className="text-xs text-gray-500">Farm Manager</p>
            </div>
          </div>
        </div>
        
        <nav className="mt-2 flex-1 overflow-y-auto pb-2">
          {sections.map((section, sIdx) => (
            <div key={section.heading} className={sIdx === 0 ? "" : "mt-4"}>
              <div className="px-6 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-gray-500">
                {section.heading}
              </div>
              {section.links.map((link) => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  end={link.path === "/"}
                  className={({ isActive }) => 
                    `sidebar-link ${isActive ? 'active' : ''}`
                  }
                  onClick={() => setIsOpen(false)}
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and')}`}
                >
                  <link.icon size={20} />
                  <span>{link.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        
        <div className="p-6 border-t border-[#27272A] space-y-3">
          <DeployInfo />
          <button
            onClick={handleLogout}
            className="w-full p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 transition-colors flex items-center justify-center gap-2"
            data-testid="logout-btn"
          >
            <LogOut size={16} />
            <span className="text-sm font-medium">Logout</span>
          </button>
        </div>
      </aside>
    </>
  );
};

// Page titles shown in the mobile header, keyed by route
const PAGE_TITLES = {
  "/": "Dashboard",
  "/customers": "Customers",
  "/admin": "Machines & Repairs",
  "/pending-machines": "Pending Machines",
  "/machine-statements": "Machine Statements",
  "/worker-history": "Worker/IP History",
  "/payments": "Payments",
  "/receipts": "Receipts",
  "/whatsapp-bot": "Bot Console",
  "/settings": "Settings",
};

// Mobile Header — shows the current section so admins always know where they are
const MobileHeader = ({ setIsOpen }) => {
  const location = useLocation();
  const title = PAGE_TITLES[location.pathname] || "WKBeast";
  return (
    <header className="fixed top-0 left-0 right-0 h-14 bg-[#0F0F0F]/95 backdrop-blur-md border-b border-[#27272A] flex items-center px-3 z-20 md:hidden">
      <button
        onClick={() => setIsOpen(true)}
        className="p-2 -ml-1 hover:bg-[#1A1A1A] active:bg-[#1A1A1A] rounded-lg shrink-0"
        data-testid="mobile-menu-btn"
        aria-label="Open menu"
      >
        <Menu size={22} />
      </button>
      <div className="flex items-center gap-2 ml-2 min-w-0">
        <div className="w-7 h-7 rounded-lg bg-[#00E054] flex items-center justify-center shrink-0">
          <span className="text-black font-bold text-sm">W</span>
        </div>
        <span className="font-bold truncate">{title}</span>
      </div>
    </header>
  );
};

// Bottom tab bar — thumb-reachable primary navigation for phones.
// The full sidebar (with every section) stays one tap away via "Menu".
const BottomNav = ({ setIsOpen }) => {
  const location = useLocation();
  const items = [
    { path: "/", icon: LayoutDashboard, label: "Home" },
    { path: "/customers", icon: Users, label: "Customers" },
    { path: "/admin", icon: Wrench, label: "Machines" },
    { path: "/payments", icon: CreditCard, label: "Payments" },
  ];
  return (
    <nav className="bottom-nav" data-testid="bottom-nav">
      {items.map((item) => {
        const isActive = item.path === "/" ? location.pathname === "/" : location.pathname.startsWith(item.path);
        return (
          <NavLink
            key={item.path}
            to={item.path}
            className={`bottom-nav-link ${isActive ? "active" : ""}`}
            data-testid={`bottom-nav-${item.label.toLowerCase()}`}
          >
            <item.icon />
            <span>{item.label}</span>
          </NavLink>
        );
      })}
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="bottom-nav-link"
        data-testid="bottom-nav-menu"
      >
        <Menu />
        <span>Menu</span>
      </button>
    </nav>
  );
};

function App() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { isAuthenticated, isLoading, login, logout } = useAuth();

  // Check if on customer portal route
  const isCustomerPortal = window.location.pathname === "/portal" || window.location.pathname.startsWith("/portal");
  // Check if on the staff/helper monitoring route (view-only, no admin login required)
  const isHelperPortal = window.location.pathname.startsWith("/helper");

  // Initialize default data on first load
  useEffect(() => {
    if (isAuthenticated && !isCustomerPortal && !isHelperPortal) {
      const initData = async () => {
        try {
          await axios.post(`${API}/init`);
        } catch (e) {
          console.error("Init error:", e);
        }
      };
      initData();
    }
  }, [isAuthenticated, isCustomerPortal]);

  // Public catalog - no login, shareable link the bot sends
  if (window.location.pathname === "/catalog" || window.location.pathname.startsWith("/catalog")) {
    return <Catalog />;
  }

  // Customer portal - separate flow
  if (isCustomerPortal) {
    return <CustomerPortal />;
  }

  // Helper/staff monitoring portal - separate flow, view-only, no admin login required
  if (isHelperPortal) {
    return <HelperPortal />;
  }

  // Show loading while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-[#00E054] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return <LoginScreen onLogin={login} />;
  }

  return (
    <div className="app-container">
      <BrowserRouter>
        <Toaster 
          position="top-right" 
          richColors 
          theme="dark"
          toastOptions={{
            style: {
              background: '#0F0F0F',
              border: '1px solid #27272A',
              color: '#FFFFFF'
            }
          }}
        />
        <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} onLogout={logout} />
        <MobileHeader setIsOpen={setSidebarOpen} />

        <main className="main-content">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/customers" element={<Customers />} />
            <Route path="/payments" element={<Payments />} />
            <Route path="/whatsapp-bot" element={<WhatsAppBot />} />
            <Route path="/receipts" element={<Receipts />} />
            <Route path="/admin" element={<AdminPanel />} />
            <Route path="/pending-machines" element={<PendingMachines />} />
            <Route path="/machine-statements" element={<MachineStatements />} />
            <Route path="/worker-history" element={<WorkerHistory />} />
            <Route path="/settings" element={<SettingsPage />} />
          </Routes>
        </main>
        <BottomNav setIsOpen={setSidebarOpen} />
      </BrowserRouter>
    </div>
  );
}

export default App;
